import { LayerType } from '@/views/map/layers/types';
import type { FC } from 'react';
import type { MantineTheme } from '@mantine/core';
import { CLEAR } from '@/views/list/NetworkTerms';
import {
  NETWORK_GROUP,
  type AnalysisFeature,
  type TagField,
} from '@/features/types';
import { GroupAnalysisType } from '@/generated/types/navigator/types.gen';
import type { CustomIconName } from '@/components/Icon/Icon';
import { convertColorArrayToString } from '@/helpers/colors';
import { CRITICAL_TREES_OVERVIEW_COLOURS } from '@/views/map/layers/criticalTrees';
import { NetworkLegend } from '@/views/map/Legend/NetworkLegend';
import { isPge } from '@/features/utils';
import { HELP_ARTICLES } from '@/helpers/helpArticles';
import {
  TRANSLUCENT_ALPHA,
  OPAQUE_ALPHA,
  TRANSPARENT_ALPHA,
  ENC_RISK_COLOUR,
  PRIORITY_COLOUR,
  UNKNOWN,
  workStatusColorEstimation,
  getPriorityFillColor,
} from './utils';
import type { ClientContext } from './clientContext';
import type { LayerSource } from '@/hooks/useDatasets';
import { type Color } from 'deck.gl';
import type { MessageId } from '@/assets/lang/en-US';
import type { WildfireMode } from '@/hooks/useWildfireMode';

type ColorConfig = {
  fillColor: (d: AnalysisFeature, e?: boolean) => Color;
  priorityColor?: (d: number, e: MantineTheme) => string;
};

export type LegendItemProps = {
  Component: FC<{
    selectedAnalysis: GroupAnalysisType;
    isZoomedOut?: boolean;
  }>;
  title?: string;
  color?: Record<string, number[]>;
};

export type AnalysisEnablementOptions = {
  hasPriority: boolean;
  /** Wildfire feature mode resolved from `flag-wildfire`. The single source of truth
   * for whether — and how — wildfire UI is exposed. */
  wildfireMode: WildfireMode;
  showClearSpanAnalysis: boolean;
  datasets: LayerSource[];
};

type AnalysisConfig = {
  title: string;
  getLabel: (c: ClientContext, options: AnalysisEnablementOptions) => MessageId;
  description: string;
  icon?: CustomIconName;
  isEnabled: (e: ClientContext, options: AnalysisEnablementOptions) => boolean;
  defaultNetworkGroup: NETWORK_GROUP;
  mapLayers: LayerType[];
  colorConfig?: ColorConfig;
  mvtUrl: (d: number, e: boolean) => void;
  getLegendItemPerAnalysis: () => LegendItemProps;
  helpLink?: string;
  listHelpLink?: string;
  helpShortText?: string;
};

const mvtTileUrl = (d: number, e: boolean) =>
  `/v1/analysis/level3/{z}/{x}/{y}?v=${d}&d=${e}`;

const getPriorityColor = (
  priorityRating: number,
  theme: MantineTheme,
  noBackground?: boolean
) => {
  switch (priorityRating) {
    case 1:
      return `${
        noBackground
          ? `background-color: ${convertColorArrayToString(PRIORITY_COLOUR.P1)}`
          : `
      background-color: ${theme.colors.redBrand[1]};
      color: ${theme.colors.redBrand[9]};
    `
      }`;
    case 2:
      return `${
        noBackground
          ? `background-color: ${convertColorArrayToString(PRIORITY_COLOUR.P2)}`
          : `background-color:  ${theme.colors.orangeBrand[1]};
        color: ${theme.colors.orangeBrand[9]};
      `
      }`;
    case 3:
      return `${
        noBackground
          ? `background-color: ${convertColorArrayToString(PRIORITY_COLOUR.P3)}`
          : `background-color: ${theme.colors.yellowBrand[1]};
        color: ${theme.colors.yellowBrand[9]};
      `
      }`;
    case 4:
      return `${
        noBackground
          ? `background-color: ${convertColorArrayToString(PRIORITY_COLOUR.P4)}`
          : `background-color:  ${theme.colors.blueBrand[0]};
        color: ${theme.colors.blueBrand[4]};
      `
      }`;
    case 5:
      return `${
        noBackground
          ? `background-color: ${convertColorArrayToString(PRIORITY_COLOUR.P5)}`
          : `background-color: ${
              noBackground ? 'none' : theme.colors.blueBrand[1]
            };
      color: ${theme.colors.blueBrand[9]};
      `
      }`;
    default:
      return `${
        noBackground
          ? `background-color: ${convertColorArrayToString(
              PRIORITY_COLOUR.unknown,
              1
            )}`
          : `background-color: ${
              noBackground ? 'none' : theme.colors.grayBrand[0]
            };
        color: ${theme.colors.grayBrand[6]};
      `
      }`;
  }
};

/**
 * Get the priority color value as a hex/rgb string for use in styles
 * @param priorityRating The priority rating (1-5)
 * @returns The color value (e.g., "#rgb(255, 0, 0)")
 */
export const getPriorityColorValue = (priorityRating: number): string =>
  convertColorArrayToString(
    PRIORITY_COLOUR[`P${priorityRating}`] || PRIORITY_COLOUR.unknown
  );

export const analysisView: Record<GroupAnalysisType, AnalysisConfig> = {
  [GroupAnalysisType.ENCROACHMENT]: {
    title: 'list.encroachmentAnalysisViewTitle',
    getLabel: () => 'list.encroachmentAnalysisView',
    description: 'list.encroachmentAnalysisViewDescription',
    icon: 'ObjectiveEncroachment',
    isEnabled: () => true,
    defaultNetworkGroup: NETWORK_GROUP.level3,
    mapLayers: [
      LayerType.base,
      LayerType.poles,
      LayerType.scan,
      LayerType.tree_type,
      LayerType.lines,
      LayerType.encroachment,
      LayerType.segmentation,
      LayerType.tree_species,
      LayerType.wildfire_risk,
      LayerType.fuel_hazard,
      LayerType.red_flag_warning,
      LayerType.worktype_analysis,
      LayerType.distance_bucket,
      LayerType.overhang,
      LayerType.trees,
      LayerType.satellite_metadata,
      LayerType.satellite_metadata_sub,
    ],
    colorConfig: {
      fillColor: (d, isOverview = false): [number, number, number, number] => {
        const lowRiskAlpha = isOverview ? TRANSLUCENT_ALPHA : TRANSPARENT_ALPHA;
        const highRiskAlpha = isOverview ? OPAQUE_ALPHA : TRANSPARENT_ALPHA;
        if (d.properties.workStatus) {
          return workStatusColorEstimation(d.properties.workStatus, isOverview);
        }
        switch (d.properties.totalEncScore) {
          case 2:
            return [...ENC_RISK_COLOUR['minor'], lowRiskAlpha];
          case 3:
            return [...ENC_RISK_COLOUR['low'], lowRiskAlpha];
          case 4:
            return [...ENC_RISK_COLOUR['medium'], highRiskAlpha];
          case 5:
            return [...ENC_RISK_COLOUR['high'], highRiskAlpha];
          case 1: // least likely
            return [...ENC_RISK_COLOUR['none'], lowRiskAlpha];
          default:
            return [...ENC_RISK_COLOUR['unknown'], lowRiskAlpha];
        }
      },
    },
    mvtUrl: (d: number, e: boolean) => mvtTileUrl(d, e),
    getLegendItemPerAnalysis: () => {
      return {
        Component: NetworkLegend,
        title: 'Spans by encroachment level',
        color: ENC_RISK_COLOUR,
      };
    },
    helpLink: HELP_ARTICLES.encroachmentRisk, //link from intercom article
    listHelpLink: HELP_ARTICLES.encroachmentRiskGroups, //link from intercom article
    helpShortText: 'Encroachment',
  },
  [GroupAnalysisType.FALL_IN]: {
    title: 'list.fallInAnalysisViewTitle',
    getLabel: () => 'list.fallInHealthAnalysisView',
    description: 'list.fallInHealthAnalysisViewDescription',
    icon: 'ObjectiveFallIn',
    helpLink: HELP_ARTICLES.fallInHealth, //link from intercom article
    listHelpLink: HELP_ARTICLES.fallInHealth, //link from intercom article
    helpShortText: 'Fall-in',
    isEnabled: (_e, options) => {
      return (
        options.datasets.findIndex(
          (d) => d.layerType === LayerType[LayerType.trees]
        ) !== -1
      );
    },
    defaultNetworkGroup: NETWORK_GROUP.level3,
    mapLayers: [
      LayerType.base,
      LayerType.poles,
      LayerType.scan,
      LayerType.lines,
      LayerType.wildfire_risk,
      LayerType.fuel_hazard,
      LayerType.worktype_analysis,
      LayerType.red_flag_warning,
      LayerType.trees,
      LayerType.satellite_metadata,
      LayerType.satellite_metadata_sub,
    ],
    colorConfig: {
      fillColor: () => {
        return [...UNKNOWN, TRANSPARENT_ALPHA];
      },
    },
    mvtUrl: (d: number, e: boolean) => mvtTileUrl(d, e),
    getLegendItemPerAnalysis: () => {
      return {
        Component: () => null,
      };
    },
  },
  [GroupAnalysisType.PRIORITY]: {
    title: 'list.priorityAnalysisViewTitle',
    getLabel: (_c, options: AnalysisEnablementOptions) =>
      // Disambiguate the reliability-priority label only when the customer also has the
      // legacy tabular WPL view alongside it (AEP-style). Other modes (fuels-only, all)
      // get their own wildfire UI surfaces, so the plain label is enough.
      options.wildfireMode === 'legacy'
        ? 'list.reliabilityPriorityAnalysisView'
        : 'list.priorityAnalysisView',
    description: 'list.priorityAnalysisViewDescription',
    icon: 'ObjectiveReliability',
    isEnabled: (_e, options: AnalysisEnablementOptions) => options.hasPriority,
    defaultNetworkGroup: NETWORK_GROUP.level3,
    mapLayers: [
      LayerType.base,
      LayerType.poles,
      LayerType.scan,
      LayerType.tree_type,
      LayerType.lines,
      LayerType.encroachment,
      LayerType.segmentation,
      LayerType.tree_species,
      LayerType.wildfire_risk,
      LayerType.fuel_hazard,
      LayerType.worktype_analysis,
      LayerType.red_flag_warning,
      LayerType.distance_bucket,
      LayerType.overhang,
      LayerType.trees,
      LayerType.satellite_metadata,
      LayerType.satellite_metadata_sub,
    ],
    colorConfig: {
      fillColor: (d, isOverview = false): [number, number, number, number] =>
        getPriorityFillColor(
          d.properties.totalPriorityScore,
          d.properties.workStatus,
          isOverview
        ),
      priorityColor: (d: number, theme: MantineTheme) =>
        getPriorityColor(d, theme),
    },
    mvtUrl: (d: number, e: boolean) => mvtTileUrl(d, e),
    getLegendItemPerAnalysis: () => {
      return {
        Component: NetworkLegend,
        title: 'Spans by reliability priority',
        color: PRIORITY_COLOUR,
      };
    },
    helpLink: HELP_ARTICLES.priorityLevels, //link from intercom article
    listHelpLink: HELP_ARTICLES.priorityLevelsGroups,
    helpShortText: 'reliability priority',
  },
  [GroupAnalysisType.WILDFIRE_PRIORITY]: {
    title: 'list.wildfirePriorityAnalysisViewTitle',
    getLabel: () => 'list.wildfirePriorityAnalysisView',
    description: 'list.wildfirePriorityAnalysisViewDescription',
    icon: 'ObjectiveWildfire',
    isEnabled: (_e, options: AnalysisEnablementOptions) =>
      options.wildfireMode !== 'none',
    defaultNetworkGroup: NETWORK_GROUP.level3,
    mapLayers: [
      LayerType.base,
      LayerType.poles,
      LayerType.scan,
      LayerType.tree_type,
      LayerType.lines,
      LayerType.encroachment,
      LayerType.segmentation,
      LayerType.tree_species,
      LayerType.wildfire_risk,
      LayerType.fuel_hazard,
      LayerType.worktype_analysis,
      LayerType.red_flag_warning,
      LayerType.distance_bucket,
      LayerType.overhang,
      LayerType.trees,
      LayerType.satellite_metadata,
      LayerType.satellite_metadata_sub,
    ],
    colorConfig: {
      fillColor: (d, isOverview = false): [number, number, number, number] =>
        getPriorityFillColor(
          d.properties.totalWildfirePriorityScore,
          d.properties.workStatus,
          isOverview
        ),
      priorityColor: (d: number, theme: MantineTheme) =>
        getPriorityColor(d, theme),
    },
    mvtUrl: (d: number, e: boolean) => mvtTileUrl(d, e),
    getLegendItemPerAnalysis: () => {
      return {
        Component: NetworkLegend,
        title: 'Spans by wildfire priority',
        color: PRIORITY_COLOUR,
      };
    },
    helpLink: HELP_ARTICLES.wildfireIntelligence, //link from intercom article
    listHelpLink: HELP_ARTICLES.wildfireIntelligence,
    helpShortText: 'Wildfire Risk',
  },
  [GroupAnalysisType.CLEAR]: {
    title: 'list.clearAnalysisViewTitle',
    getLabel: () => 'list.clearAnalysisView',
    description: 'list.clearAnalysisViewDescription',
    isEnabled: (_e, options: AnalysisEnablementOptions) =>
      options.showClearSpanAnalysis,
    defaultNetworkGroup: NETWORK_GROUP.level3,
    mapLayers: [
      LayerType.base,
      LayerType.poles,
      LayerType.scan,
      LayerType.lines,
      LayerType.encroachment,
      LayerType.distance_bucket,
      LayerType.overhang,
      LayerType.trees,
      LayerType.segmentation,
      LayerType.critical_trees,
      LayerType.satellite_metadata,
      LayerType.satellite_metadata_sub,
    ],
    colorConfig: {
      fillColor: (d, isOverview = false) => {
        if (d.properties.workStatus) {
          return workStatusColorEstimation(d.properties.workStatus, isOverview);
        }

        const tags: TagField[] = d.properties.tags
          ? JSON.parse(d.properties.tags)
          : [];
        const isClear = tags.find((tag) => tag.name === CLEAR);

        if (d.properties.totalEncScore === 1 && isClear) {
          return [
            ...ENC_RISK_COLOUR['none'],
            isOverview ? TRANSLUCENT_ALPHA : TRANSPARENT_ALPHA,
          ];
        }

        return [...UNKNOWN, TRANSPARENT_ALPHA];
      },
    },
    mvtUrl: (d: number, e: boolean) => mvtTileUrl(d, e),
    getLegendItemPerAnalysis: () => ({ Component: NetworkLegend }),
  },
  [GroupAnalysisType.WORKLOAD_ANALYSIS]: {
    title: 'list.workloadAnalysisViewTitle',
    getLabel: (client) =>
      isPge(client)
        ? 'list.workloadAnalysisView'
        : 'list.criticalTreesInventoryView',
    description: 'list.workloadAnalysisViewDescription',
    helpLink: HELP_ARTICLES.criticalTrees,
    isEnabled: (_e, options) =>
      options.datasets.some((d) => d.layerType === 'critical_trees'),
    defaultNetworkGroup: NETWORK_GROUP.level2,
    mapLayers: [
      LayerType.base,
      LayerType.scan,
      LayerType.poles,
      LayerType.lines,
      LayerType.trees,
      LayerType.segmentation,
      LayerType.tree_species,
      LayerType.encroachment,
      LayerType.critical_trees,
      LayerType.red_flag_warning,
      LayerType.wildfire_risk,
      LayerType.fuel_hazard,
      LayerType.satellite_metadata,
      LayerType.satellite_metadata_sub,
    ],
    colorConfig: {
      fillColor: (d, isOverview = false): [number, number, number, number] => {
        const alpha = isOverview ? OPAQUE_ALPHA : TRANSPARENT_ALPHA;
        const criticalTreesCount = d.properties.criticalTreesCount ?? 0;
        if (criticalTreesCount > 5) {
          return [...CRITICAL_TREES_OVERVIEW_COLOURS[0], alpha];
        }
        if (criticalTreesCount > 3) {
          return [...CRITICAL_TREES_OVERVIEW_COLOURS[1], alpha];
        }
        if (criticalTreesCount > 0) {
          return [...CRITICAL_TREES_OVERVIEW_COLOURS[2], alpha];
        }
        return [...CRITICAL_TREES_OVERVIEW_COLOURS[3], alpha];
      },
    },
    mvtUrl: (d: number, e: boolean) => mvtTileUrl(d, e),
    getLegendItemPerAnalysis: () => ({ Component: NetworkLegend }),
  },
};
